<script lang="ts">
	import { resolve } from '$app/paths';
	// Most of your app wide CSS should be put in this file
	import '../app.css';

	import { page } from '$app/state';
	import { Layout } from '../../shared/lib/enums';
	import type { Page } from '@sveltejs/kit';
	import { particlesInit } from '$lib/shared/ui/particles';
	import { loadFull } from 'tsparticles';
	// PopupAccountBox is now wired per-layout (DefaultLayout slots it into
	// AvatarComponent's <Popup />); the root-level standalone render is gone.
	import AdminLayout from '$lib/app/layouts/AdminLayout.svelte';
	import AdminPanelLayout from '$lib/app/layouts/AdminPanelLayout.svelte';
	import TipLayout from '$lib/app/layouts/TipLayout.svelte';
	import SideNavBar from '$lib/features/admin/main/ui/SideNavBar.svelte';
	import DefaultLayout from '$lib/app/layouts/DefaultLayout.svelte';
	import { pageTransitioning } from '$lib/shared/lib/stores';
	import { fade } from 'svelte/transition';
	import { initializeAnalytics, trackPageView } from '$lib/shared/lib/analytics';
	import { browser } from '$app/environment';
	import OnboardLayout from '../layouts/OnboardLayout.svelte';
	import ToastGroupMount from '$components/ToastGroupMount.svelte';
	import { Dialog } from 'bits-ui';
	import { drawerOpen } from '$lib/shared/lib/drawerStore';

	const logoBlackSrc = '/static/images/logo/supertip-revised-black.avif';
	const logoWhiteSrc = '/static/images/logo/supertip-revised-white.avif';

	let { data, children } = $props();

	let overlay = $derived(
		(page.route.id &&
			(page.route.id.startsWith('/admin/[tag]/overlay') || page.route.id.startsWith('/o/'))) ||
			!page.route.id
	);

	function layoutFromPage(page: Page): Layout {
		if (!page.route.id) {
			return Layout.Default;
		}

		if (page.route.id.startsWith('/admin/[tag]/overlay') || page.route.id.startsWith('/o/')) {
			return Layout.None;
		} else if (page.route.id.startsWith('/admin/[tag]/panel')) {
			return Layout.Panel;
		} else if (page.route.id.startsWith('/admin')) {
			return Layout.Admin;
		} else if (page.route.id.startsWith('/signin')) {
			return Layout.None;
		} else if (page.route.id.startsWith('/diagnostics')) {
			return Layout.None;
		} else if (page.route.id.startsWith('/onboard')) {
			return Layout.Onboard;
		} else if (page.route.id.startsWith('/[tag]')) {
			return Layout.Tip;
		}

		return Layout.Default;
	}

	let layout = $derived(layoutFromPage(page));
	$effect(() => {
		if (page) {
			// Timeout lets the transition actually work
			setTimeout(() => {
				pageTransitioning.set(false);
			}, 0);

			// Initialize GA (idempotent) and track the page view together so a
			// page view is never sent before window.gtag exists. Skipped on overlay
			// routes (embedded widgets that must not load analytics).
			if (browser && !overlay && page.url) {
				initializeAnalytics();
				trackPageView(page.url);
			}
		}
	});

	void particlesInit(async (engine) => {
		await loadFull(engine);
	});
</script>

<ToastGroupMount />

<!-- Mobile-view drawer — Bits UI Dialog with side-positioned content.
     Replaces Skeleton 2's <Drawer> (no Skel 4 equivalent). State lives in
     the module-level `drawerOpen` writable in `$lib/shared/lib/drawerStore`. -->
<Dialog.Root bind:open={$drawerOpen}>
	<Dialog.Portal>
		<Dialog.Overlay class="drawer-slide-overlay fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" />
		<Dialog.Content
			class="drawer-slide-content bg-surface-50-900 fixed inset-y-0 left-0 z-50 h-dvh w-4/6 overflow-y-auto border-r border-primary-500/50 shadow-lg"
		>
			<aside class="flex h-full flex-col space-y-6 p-10">
				<a href={resolve('/')} aria-label="Go to home page">
					<img alt="Home" src={logoBlackSrc} class="block h-16 dark:hidden" />
					<img alt="Home" src={logoWhiteSrc} class="hidden h-16 dark:block" />
				</a>
				<SideNavBar user={data.user} />
			</aside>
		</Dialog.Content>
	</Dialog.Portal>
</Dialog.Root>

<!-- Page Transition Overlay — skipped for overlay routes so the transparent
     overlay (/o/[short_id], /admin/[tag]/overlay) never flashes a solid
     surface background over the stream. -->
{#if $pageTransitioning && !overlay}
	<div transition:fade={{ duration: 200 }} class="fixed inset-0 z-[9999] bg-surface-50-950"></div>
{/if}

<div class="relative z-10 {overlay ? 'bg-transparent' : 'bg-surface-50-950'}">
	{#if layout == Layout.Admin}
		<AdminLayout user={data.user}>
			{@render children?.()}
		</AdminLayout>
	{:else if layout == Layout.None}
		{@render children?.()}
	{:else if layout == Layout.Panel}
		<AdminPanelLayout user={data.user}>
			{@render children?.()}
		</AdminPanelLayout>
	{:else if layout == Layout.Tip}
		<TipLayout user={data.user}>
			{@render children?.()}
		</TipLayout>
	{:else if layout == Layout.Onboard}
		<OnboardLayout user={data.user}>
			{@render children?.()}
		</OnboardLayout>
	{:else}
		<DefaultLayout user={data.user}>
			{@render children?.()}
		</DefaultLayout>
	{/if}
</div>
